import { heapEvent } from "./React/heapEvent";
import { HeapInternalProjectId, HeapProjectId, } from "./types";
class HeaperEnvironment {
    #internalProducts = false;
    #promotionLevel;
    #heaperIdentity;
    #heap = true;
    #intercom = true;
    #silent = false;
    #multiPageApp = false;
    #extendEventProps;
    #appName;
    #appPlatform = "web";
    #accessToken;
    #idToken;
    #heapInstance;
    #intercomInstance;
    #launchDarklyClientSideId;
    #launchDarklyInstance;
    #datadogRumInstance;
    #screenDimensionsGetter;
    #heapEventQueue = [];
    constructor() {
        // do nothing
    }
    setInternalProducts(internalProducts) {
        this.#internalProducts = internalProducts;
    }
    getInternalProducts() {
        return this.#internalProducts;
    }
    /** idToken must be defined for Auth0 JWT (optional for legacy user_auth_tokens) */
    setAuthHeaders(accessToken, idToken) {
        if (accessToken && accessToken.length !== 36 && !idToken) {
            throw new Error("Auth0 JWT detected without an idToken defined! idToken must also be passed for JWT in order to detect mimic usage.");
        }
        this.#accessToken = accessToken;
        this.#idToken = idToken;
    }
    getAuthHeaders() {
        if (!this.#idToken) {
            return {
                Authorization: `Bearer ${this.#accessToken}`,
            };
        }
        return {
            Authorization: `Bearer ${this.#accessToken}`,
            IdToken: this.#idToken,
        };
    }
    setHeaperIdentity(identity) {
        this.#heaperIdentity = identity;
    }
    getHeaperIdentity() {
        return this.#heaperIdentity;
    }
    setSilent(silent) {
        this.#silent = silent;
    }
    getSilent() {
        return this.getPromotionLevel() === "Production" ? true : this.#silent;
    }
    setAppName(appName) {
        this.#appName = appName;
    }
    getAppName() {
        return this.#appName;
    }
    setAppPlatform(appPlatform) {
        this.#appPlatform = appPlatform;
    }
    getAppPlatform() {
        return this.#appPlatform;
    }
    getAppPlatformHeaders() {
        return {
            "Authorization-App-Platform": this.getAppPlatform(),
        };
    }
    setPromotionLevel(promotionLevel) {
        this.#promotionLevel = promotionLevel;
    }
    getPromotionLevel() {
        if (!this.#promotionLevel) {
            throw new Error("HeaperEnvironment.#promotionLevel has not been set.  Use setPromotionLevel(PromotionLevel) to set it.");
        }
        return this.#promotionLevel;
    }
    setExtendEventProps(extendEventProps) {
        this.#extendEventProps = extendEventProps;
    }
    getExtendEventProps() {
        return this.#extendEventProps;
    }
    setMultiPageApp(multiPageApp) {
        this.#multiPageApp = multiPageApp;
    }
    getMultiPageApp() {
        return this.#multiPageApp;
    }
    /* istanbul ignore next */
    currentMultiPageAppSessionKey() {
        return ["heaper", "multiPageApp", this.getAppName(), "timestamp"].join(":");
    }
    /* istanbul ignore next */
    updateCurrentMultiPageAppSession() {
        if (this.getMultiPageApp()) {
            // update localStorage with current timestamp
            localStorage.setItem(this.currentMultiPageAppSessionKey(), new Date().toISOString());
        }
    }
    /* istanbul ignore next */
    hasCurrentMultiPageAppSession() {
        const timestamp = localStorage.getItem(this.currentMultiPageAppSessionKey());
        if (!timestamp) {
            return false;
        }
        // consider session active if last timestamp update less than 30 minutes ago
        if (new Date().getTime() - new Date(timestamp).getTime() < 30 * 60 * 1000) {
            return true;
        }
        return false;
    }
    setHeap(heap) {
        this.#heap = heap;
    }
    getHeap() {
        return this.#heap;
    }
    setHeapInstance(heapInstance) {
        this.#heapInstance = heapInstance;
    }
    getHeapInstance() {
        if (this.#heapInstance) {
            return this.#heapInstance;
        }
        else if (window?.heap) {
            return window.heap;
        }
        else {
            throw new Error("HeaperEnvironment.#heapInstance is undefined!");
        }
    }
    getHeapProjectId() {
        return this.getInternalProducts()
            ? HeapInternalProjectId[this.getPromotionLevel()]
            : HeapProjectId[this.getPromotionLevel()];
    }
    validateHeapEnv() {
        const correctId = this.getHeapProjectId();
        const heap = this.getHeapInstance();
        if (this.getHeap()) {
            /* istanbul ignore next */
            if (heap.setAppId) {
                heap.setAppId(String(correctId));
                /* istanbul ignore next */
            }
            else if (!heap.appid || Number(heap.appid) !== correctId) {
                throw new Error(`Heap project id mismatch! The detected Heap Project Id is ${heap.appid}, but the correct Heap Project Id for ${this.getPromotionLevel()} is ${correctId}!`);
            }
        }
    }
    setIntercom(intercom) {
        this.#intercom = intercom;
    }
    getIntercom() {
        return this.#intercom;
    }
    validateIntercomEnv() {
        /* istanbul ignore next */
        if (this.getIntercom() && !this.getIntercomInstance()) {
            throw new Error(`Intercom not detected! Did you forget to add the Intercom js snippet to the <head>?`);
        }
    }
    setIntercomInstance(intercomInstance) {
        this.#intercomInstance = intercomInstance;
    }
    getIntercomInstance() {
        if (this.#intercomInstance) {
            return this.#intercomInstance;
        }
        else if (window?.Intercom) {
            return window.Intercom;
        }
        else {
            throw new Error("HeapEnvironment.#intercomInstance is undefined!");
        }
    }
    setLaunchDarklyClientSideId(launchDarklyClientSideId) {
        this.#launchDarklyClientSideId = launchDarklyClientSideId;
    }
    getLaunchDarklyClientSideId() {
        return this.#launchDarklyClientSideId;
    }
    setLaunchDarklyInstance(launchDarklyClient) {
        this.#launchDarklyInstance = launchDarklyClient;
    }
    getLaunchDarklyInstance() {
        return this.#launchDarklyInstance;
    }
    setDatadogRumInstance(datadogRumInstance) {
        this.#datadogRumInstance = datadogRumInstance;
    }
    getDatadogRumInstance() {
        return this.#datadogRumInstance;
    }
    setScreenDimensionsGetter(fn) {
        this.#screenDimensionsGetter = fn;
    }
    getScreenDimensionsGetter() {
        return this.#screenDimensionsGetter;
    }
    getScreenDimensions() {
        const getter = this.#screenDimensionsGetter;
        if (getter) {
            return getter();
        }
        if (window && window.screen) {
            return `${window.screen.width} x ${window.screen.height}`;
        }
        return "unknown";
    }
    enqueueHeapEvent(params) {
        this.#heapEventQueue.push(params);
    }
    drainHeapEventQueue() {
        while (this.#heapEventQueue.length) {
            const params = this.#heapEventQueue.shift();
            heapEvent(...params);
        }
    }
}
/**
 * @hidden
 */
export const createEnv = () => new HeaperEnvironment();
export const env = createEnv();
//# sourceMappingURL=env.js.map